import { RentalType } from 'utils/constants';

export const revenueRentalType = {
    MONTHLY: RentalType.Monthly,
    HOURLY: RentalType.Hourly,
    FLAT_RATE: 'flat_rate',
    ENTERPRISE: RentalType.Enterprise,
    DAILY: RentalType.Daily,
    BULK_RENTAL: RentalType.BulkRental,
    // Tickets/violations aren't a rental type, but they render as their own category
    // (labeled "Violations") in the "Gross Revenue by Type" graph. The backing report
    // only returns this `ticket` bucket for spots that share ticket revenue with the
    // owner; for other spots it's absent and the series reads as 0.
    TICKET: 'ticket',
    // Adjustments aren't a rental type either, but they are owner revenue, so the
    // backing report returns them as their own `adjustment` bucket.
    ADJUSTMENT: 'adjustment',
} as const;

export type RevenueRentalType = (typeof revenueRentalType)[keyof typeof revenueRentalType];

type RevenueReport = {
    gross_revenue: number;
    net_revenue: number;
    formatted_gross_revenue: string;
    formatted_net_revenue: string;
};

export type RevenueByMonth = Array<
    {
        date: string;
        // Minimum guarantee amount (cents) in effect for this month;
        // 0 if the spot has no minimum guarantee deal for that month.
        minimum_guarantee_amount: number;
    } & RevenueReport
>;

export type RevenueByDay = Array<
    {
        date: string;
    } & Pick<RevenueReport, 'gross_revenue' | 'net_revenue'>
>;

// `ticket` is optional: the report only returns it for spots that share ticket revenue
// with the owner. `adjustment` is optional because the dashboard and backend deploy
// independently, so a new dashboard can briefly receive the old response shape without
// it. Consumers must guard against both being absent.
type OptionalRevenueRentalType = typeof revenueRentalType.TICKET | typeof revenueRentalType.ADJUSTMENT;

export type RevenueReportByRentalType = Array<
    { date: string } & { [key in Exclude<RevenueRentalType, OptionalRevenueRentalType>]: RevenueReport } & {
        [key in OptionalRevenueRentalType]?: RevenueReport;
    }
>;

export enum ReportDataType {
    TRANSACTIONS_V2 = 'transactions_v2',
    TRANSACTIONS_V1 = 'transactions_v1',
    HISTORICAL = 'historical',
}

export type YoYRevenue = {
    reports: { date: string; gross_revenue: number; net_revenue: number; data_type: ReportDataType }[];
    uplift: {
        current_average: number;
        prior_average: number;
        growth_percent: number;
        yoy_gross_revenue_date: string;
        yoy_gross_revenue: number;
        yoy_gross_revenue_percent: number;
    };
};

type RevenueByDate = { date: string; cumulative_gross_revenue: number };

export type PacingReport = {
    current_month: RevenueByDate[];
    previous_month: RevenueByDate[];
    current_month_last_year: RevenueByDate[];
    uplift: {
        current_month_to_date: string;
        current_month_to_date_gross_revenue: number;
    };
};
